import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { DataService } from '@data/data.service';
//
import { ButtonCellRendererComponent } from '@grid-cell/button-cell-renderer.component';
import { getRowStyle } from '@grid/orders-grid/orders-grid.component';
import { NotificationService } from '@notification/notification.service';
import {
  GlobalEventServiceService,
  GlobalEventType,
} from '@services/global-event-service.service';
import {
  isValidDate,
  toDateForTimepicker,
  toRequestDate,
  toRequestTime,
  validateTime,
} from '@util/date-utils';
import { cloneDeep, toName } from '@util/utils';
import { BackendPaths } from '@common/backend-paths';
import { OrderCustomerRenderer } from '@common/order-customer-renderer';
import { getMontageAddress } from '@util/order-utils';
import { StateService } from '@services/state.service';
import { MyLog } from '@common/common';

const statusAssigned = {
  id: 1,
  text: 'Zugewiesen',
};

const statusNotAssigned = {
  id: 2,
  text: 'Nicht zugewiesen',
};

const statusAll = {
  id: 3,
  text: 'Alle',
};

const assignColumn = {
  width: 120,
  cellRenderer: ButtonCellRendererComponent,
  cellRendererParams: {
    label: 'Auswählen',
  },
  headerClass: '',
  pinned: 'left',
  headerName: '',
};

const idColumn: any = {
  field: 'id',
  headerName: 'ID',
  width: 40,
};

const teamColumn: any = {
  field: 'team_name',
  headerName: 'Team',
  width: 120,
};

const dateColumn: any = {
  field: 'work_day',
  headerName: 'Datum',
  width: 100,
};

const customerColumn: any = {
  headerName: 'Kunde',

  cellRenderer: OrderCustomerRenderer,
};

@Component({
  selector: 'app-team-date',
  templateUrl: './team-date.component.html',
})
export class TeamDateComponent implements OnInit {
  d: any;

  startTime: any;
  endTime: any;

  status = 3;

  statuses: any[] = [statusAssigned, statusNotAssigned, statusAll];

  statusFC = new UntypedFormControl();

  canDelete = false;

  canSave = false;

  searchPerformed = false;

  searchValue = '';

  order: any;

  noRowsTemplate = 'Keine Aufträge gefunden';

  gridOptions: any = {
    defaultColDef: {
      headerClass: 'def-col',
      autoHeight: true,
    },
    columnDefs: [
      assignColumn,
      idColumn,
      teamColumn,
      customerColumn,
      dateColumn,
    ],
    getRowStyle,
  };

  rowData: any[] = [];

  dialogData: any;

  constructor(
    private dataService: DataService,
    public dialogRef: MatDialogRef<TeamDateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private eventService: GlobalEventServiceService,
    private notificationService: NotificationService,
    private stateService: StateService
  ) {
    this.dialogData = cloneDeep(data);

    this.d = data.d;

    const sd = new Date();
    sd.setHours(9);
    sd.setMinutes(0);
    sd.setSeconds(0);
    this.startTime = sd;

    const ed = new Date();
    ed.setHours(11);
    ed.setMinutes(0);
    ed.setSeconds(0);
    this.endTime = ed;
  }

  eventSub!: Subscription;

  orderFixed = false;

  montageLocationUnknown = false;

  ngOnInit(): void {
    if (this.data.order) {
      const sd = toDateForTimepicker(this.data.order.work_from);
      const ed = toDateForTimepicker(this.data.order.work_to);
      if (sd && ed) {
        this.startTime = sd;
        this.endTime = ed;
      }
    }

    this.order = this.data.order;
    this.orderFixed = !!this.data.fixedOrder;
    //MyLog.log('Fixed order:', this.order, this.orderFixed);

    if (this.order) {
      const loctype = getMontageAddress(this.order);
      this.montageLocationUnknown = !(loctype >= 0 && loctype <= 2);
    }

    this.canDelete = !!this.order;
    this.validateFields();

    this.statusFC.setValue(statusNotAssigned);
    const eventType = 'assigned';

    this.eventSub = this.eventService
      .listen(eventType)
      .subscribe((event: GlobalEventType) => {
        if (event.type === eventType && event.data) {
          this.assignedOrderById(event.data);
        }
      });

    this.canSave = !!this.stateService.isAdmin();
  }

  close(result: any = null) {
    this.dialogRef.close(result);
  }

  save() {
    let stRes = validateTime(this.startTime);
    if (stRes.success) {
      this.startTime = stRes.date;
    }

    let etRes = validateTime(this.endTime);
    if (etRes.success) {
      this.endTime = etRes.date;
    }

    if (!stRes.success || !etRes.success) {
      this.notificationService.showErrors(['Uhrzeiten falsch!']);
      return;
    }

    const st = toRequestTime(this.startTime);
    const et = toRequestTime(this.endTime);

    if (this.d && st !== null && et !== null) {
      const url = BackendPaths.ORDERS_FULL + this.order.id;

      if (this.order?.id) {
        const data: any = {
          op: 'savedate',
          id: this.order.id,
          work_day: toRequestDate(this.d),
          work_from: st,
          work_to: et,
        };

        if (this.data.team?.id) {
          data.team_id = this.data.team.id;
        }

        this.dataService.post(url, data).subscribe((result: any) => {
          const res = result?.result;

          if (res?.success) {
            if (res.msg) {
              this.notificationService.showSuccess(res.msg);
            }
            this.close(res.data);
            return;
          }
          this.notificationService.showErrors(res?.errors);
        });
      } else {
        this.close({
          newDate: true,
          work_day: toRequestDate(this.d),
          work_from: st,
          work_to: et,
        });
      }
    } else {
      this.notificationService.showErrors(['Eingaben ungültig']);
    }
  }

  deleteDate() {
    if (this.canSave && this.canDelete && this.order?.id) {
      const data: any = {
        op: 'deletedate',
        id: this.order.id,
      };
      const url = '/montage/orders/' + this.order.id;

      this.dataService.post(url, data).subscribe((result: any) => {
        const res = result.result;
        if (res?.success) {
          if (res.msg) {
            this.notificationService.showSuccess(res.msg);
          }
          this.close({
            deletedDate: true,
            orderId: this.order.id,
          });
          return;
        }
        this.notificationService.showErrors(res?.errors);
      });
    } else {
      this.close({
        deletedDate: true,
        noOrder: true,
      });
    }
  }

  onDateChange($event: any) {
    MyLog.log('Date change:', $event);
    this.d = $event;
    this.validateFields();
  }

  timeChangeHandler($event: any, isStartDate: boolean) {
    console.log('Time change:', $event, isStartDate);

    if (isStartDate) {
      this.startTime = $event;
    } else {
      this.endTime = $event;
    }

    this.validateFields();
  }

  invalidInputHandler(isStartDate: boolean) {
    MyLog.log('INvalid:', isStartDate);
  }

  isValidTime(time: any) {
    const result = validateTime(time);
    return result.success;
  }

  validateFields() {
    this.canSave =
      !!this.stateService.isAdmin() &&
      this.isValidTime(this.startTime) &&
      this.isValidTime(this.endTime) &&
      isValidDate(this.d) &&
      this.validateSelectedOrder();
  }

  validateSelectedOrder() {
    return !!this.order;
  }

  assignedOrderById(id: any) {
    const order = this.rowData.find((item: any) => {
      return item.id === id;
    });

    this.order = order;
    this.validateFields();
  }

  search() {
    this.searchPerformed = true;

    const url = BackendPaths.ORDERS;
    const status = this.statusFC.value?.id;

    const data: any = {
      op: 'searchorders',
      name: this.searchValue,
      status,
    };

    this.dataService.post(url, data).subscribe((result: any) => {
      const res = result.result;

      if (res?.success) {
        if (Array.isArray(res.rows)) {
          res.rows.forEach((item: any) => {
            if (!item.team_name && item.team) {
              item.team_name = toName(item.team);
            }
          });
          this.rowData = res.rows;
        } else {
          this.rowData = [];
        }
        return;
      }
      this.notificationService.showErrors(res.errors);
    });
  }
}
